import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaTimes,
  FaShoppingCart,
  FaBoxOpen,
  FaFileAlt,
  FaTrash,
  FaPlus,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import '../styles/InventoryProcurement.css';

const CREATE = `
  mutation CreatePO($createPurchaseOrderInput: CreatePurchaseOrderInput!) {
    createPurchaseOrder(createPurchaseOrderInput: $createPurchaseOrderInput) {
      id poNumber status supplierEmailSent supplierEmailError stockInPosted stockInMessage
    }
  }
`;

const UPDATE = `
  mutation UpdatePO($updatePurchaseOrderInput: UpdatePurchaseOrderInput!) {
    updatePurchaseOrder(updatePurchaseOrderInput: $updatePurchaseOrderInput) {
      id poNumber status supplierEmailSent supplierEmailError stockInPosted stockInMessage
    }
  }
`;

const FIND_SUPPLIERS = `
  query POSuppliers {
    findAllSuppliers { id name code status email }
  }
`;

const FIND_ITEMS = `
  query POItems {
    findAllItem { id name code perItemCost totalCost quantity unit supplier preferredSupplier }
  }
`;

const STATUSES = [
  { value: 'Draft', label: 'Draft' },
  { value: 'PendingApproval', label: 'Pending Approval' },
  { value: 'Ordered', label: 'Ordered' },
  { value: 'PartiallyReceived', label: 'Partially Received' },
  { value: 'Received', label: 'Received' },
  { value: 'Cancelled', label: 'Cancelled' },
];

function emptyLine() {
  return { itemId: '', description: '', quantity: '1', quantityReceived: '0', unitCost: '' };
}

function catalogUnitCost(item) {
  const direct = Number(item?.perItemCost);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const qty = Number(item?.quantity);
  const total = Number(item?.totalCost);
  if (Number.isFinite(qty) && qty > 0 && Number.isFinite(total) && total > 0) {
    return total / qty;
  }
  return Number.isFinite(direct) && direct >= 0 ? direct : 0;
}

function applyCatalogItem(line, item) {
  if (!item) return line;
  const cost = catalogUnitCost(item);
  return {
    ...line,
    itemId: item.id,
    description: item.name || line.description,
    unitCost: cost > 0 ? String(cost) : '',
  };
}

function nextPoPreview() {
  const year = new Date().getFullYear();
  return `PO-${year}-xxx`;
}

export default function PurchaseOrderForm({
  editData = null,
  prefill = null,
  onClose,
  onSaved,
}) {
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const itemsRef = useRef([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    supplierId: editData?.supplierId || editData?.supplier?.id || '',
    orderDate: editData?.orderDate
      ? String(editData.orderDate).slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    expectedDelivery: editData?.expectedDelivery
      ? String(editData.expectedDelivery).slice(0, 10)
      : '',
    requestedBy: editData?.requestedBy || '',
    status: editData?.status || prefill?.status || 'Draft',
    deliveryInstructions: editData?.deliveryInstructions || '',
    notes: editData?.notes || prefill?.notes || '',
    lines: editData?.lines?.length
      ? editData.lines.map((l) => ({
          itemId: l.itemId || '',
          description: l.description || '',
          quantity: String(l.quantity || 1),
          quantityReceived: String(l.quantityReceived ?? 0),
          unitCost: String(l.unitCost ?? 0),
        }))
      : prefill?.itemId || prefill?.description
        ? [
            {
              itemId: prefill.itemId || '',
              description: prefill.description || '',
              quantity: String(prefill.quantity || 1),
              quantityReceived: '0',
              unitCost: String(prefill.unitCost ?? 0),
            },
          ]
        : [emptyLine()],
  }));

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      graphqlRequest(FIND_SUPPLIERS),
      graphqlRequest(FIND_ITEMS),
    ])
      .then(([supRes, itemRes]) => {
        if (cancelled) return;
        const catalog = Array.isArray(itemRes?.findAllItem)
          ? itemRes.findAllItem
          : [];
        itemsRef.current = catalog;
        setSuppliers(supRes?.findAllSuppliers || []);
        setItems(catalog);
        setForm((prev) => ({
          ...prev,
          lines: prev.lines.map((line) => {
            if (!line.itemId) return line;
            const item = catalog.find((i) => i.id === line.itemId);
            if (!item) return line;
            const existing = Number(line.unitCost);
            if (Number.isFinite(existing) && existing > 0) {
              return {
                ...line,
                description: line.description || item.name,
              };
            }
            return applyCatalogItem(line, item);
          }),
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.message || 'Failed to load suppliers or products');
        setSuppliers([]);
        setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (key, value) => {
    if (key === 'status' && value === 'Received') {
      setForm((prev) => ({
        ...prev,
        status: value,
        lines: prev.lines.map((l) => ({
          ...l,
          quantityReceived: String(Math.max(1, Math.round(Number(l.quantity) || 1))),
        })),
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setLine = (index, key, value) => {
    setForm((prev) => {
      const lines = [...prev.lines];
      lines[index] = { ...lines[index], [key]: value };
      if (key === 'itemId' && value) {
        const item = itemsRef.current.find((i) => i.id === value);
        if (item) {
          lines[index] = applyCatalogItem(lines[index], item);
        }
      }
      return { ...prev, lines };
    });
  };

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s.id === form.supplierId) || null,
    [suppliers, form.supplierId],
  );

  const catalogItems = useMemo(() => {
    const supplierName = String(selectedSupplier?.name || '')
      .trim()
      .toLowerCase();
    if (!supplierName) return items;
    const rank = (it) => {
      const hay = `${it.supplier || ''} ${it.preferredSupplier || ''}`.toLowerCase();
      return hay.includes(supplierName) ? 0 : 1;
    };
    return [...items].sort((a, b) => rank(a) - rank(b) || String(a.name).localeCompare(String(b.name)));
  }, [items, selectedSupplier]);

  const totals = useMemo(() => {
    const subtotal = form.lines.reduce((sum, l) => {
      const qty = Number(l.quantity) || 0;
      const cost = Number(l.unitCost) || 0;
      return sum + qty * cost;
    }, 0);
    const gst = subtotal * 0.1;
    return { subtotal, gst, total: subtotal + gst };
  }, [form.lines]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplierId) {
      toast.error('Select a supplier');
      return;
    }
    const lines = form.lines
      .filter((l) => l.description.trim())
      .map((l) => ({
        itemId: l.itemId || undefined,
        description: l.description.trim(),
        quantity: Math.max(1, Math.round(Number(l.quantity) || 1)),
        quantityReceived: Math.max(0, Math.round(Number(l.quantityReceived) || 0)),
        unitCost: Number(l.unitCost) || 0,
      }));
    if (!lines.length) {
      toast.error('Add at least one line item');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        supplierId: form.supplierId,
        orderDate: form.orderDate || undefined,
        expectedDelivery: form.expectedDelivery || undefined,
        requestedBy: form.requestedBy.trim() || undefined,
        status: form.status,
        deliveryInstructions: form.deliveryInstructions.trim() || undefined,
        notes: form.notes.trim() || undefined,
        lines,
      };
      const saved = editData?.id
        ? (
            await graphqlRequest(
              UPDATE,
              { updatePurchaseOrderInput: { id: editData.id, ...payload } },
              { timeout: 60000 },
            )
          )?.updatePurchaseOrder
        : (
            await graphqlRequest(
              CREATE,
              { createPurchaseOrderInput: payload },
              { timeout: 60000 },
            )
          )?.createPurchaseOrder;
      const verb = editData?.id ? 'updated' : 'created';
      const status = saved?.status || payload.status;
      if (status === 'Received') {
        if (saved?.stockInPosted && saved?.stockInMessage) {
          toast.success(`Purchase order ${verb}. ${saved.stockInMessage}`);
        } else if (saved?.stockInMessage) {
          toast.warn(saved.stockInMessage);
        } else {
          toast.success(`Purchase order ${verb} as Received.`);
        }
      } else if (status === 'Ordered' && saved?.supplierEmailSent) {
        toast.success(`Purchase order ${verb} and emailed to the supplier`);
      } else if (status === 'Ordered' && saved?.supplierEmailError) {
        toast.warn(saved.supplierEmailError);
      } else if (status === 'Ordered') {
        toast.success(
          `Purchase order ${verb}. Add an email on the supplier to send it automatically.`,
        );
      } else {
        toast.success(
          `Purchase order saved as ${status || 'draft'}. Set status to Ordered to email the supplier.`,
        );
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to save purchase order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="iproc-overlay" onClick={onClose}>
      <div className="iproc-modal" onClick={(e) => e.stopPropagation()}>
        <header className="iproc-modal-header">
          <h2>{editData ? 'Edit Purchase Order' : 'Create Purchase Order'}</h2>
          <button type="button" className="iproc-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </header>
        <form className="iproc-modal-body" onSubmit={submit}>
          <section>
            <h3><FaShoppingCart /> PO Information</h3>
            <div className="iproc-grid">
              <label className="iproc-field">
                <span>PO Number</span>
                <input value={editData?.poNumber || nextPoPreview()} readOnly />
              </label>
              <label className="iproc-field">
                <span>Date</span>
                <input type="date" value={form.orderDate} onChange={(e) => set('orderDate', e.target.value)} />
              </label>
              <label className="iproc-field">
                <span>Supplier <em>*</em></span>
                <select value={form.supplierId} onChange={(e) => set('supplierId', e.target.value)} required>
                  <option value="">Select supplier</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.email ? ` — ${s.email}` : ' — no email'}
                    </option>
                  ))}
                </select>
                {selectedSupplier ? (
                  <small className="iproc-hint">
                    {selectedSupplier.email
                      ? `Ordered emails this PO to ${selectedSupplier.email}.`
                      : 'This supplier has no email. Add one on the supplier record first.'}
                  </small>
                ) : null}
              </label>
              <label className="iproc-field">
                <span>Expected Delivery</span>
                <input type="date" value={form.expectedDelivery} onChange={(e) => set('expectedDelivery', e.target.value)} />
              </label>
              <label className="iproc-field">
                <span>Requested By</span>
                <input value={form.requestedBy} onChange={(e) => set('requestedBy', e.target.value)} placeholder="Staff name" />
              </label>
              <label className="iproc-field">
                <span>Status</span>
                <select value={form.status} onChange={(e) => set('status', e.target.value)}>
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <small className="iproc-hint">
                  Ordered emails this PO to the supplier. Received adds the
                  linked products into stock.
                </small>
              </label>
            </div>
          </section>

          <section>
            <h3><FaBoxOpen /> Line Items</h3>
            <small className="iproc-hint">
              Select a product on each line so Received can add that quantity to
              stock automatically.
            </small>
            {form.lines.map((line, index) => {
              const lineTotal =
                (Number(line.quantity) || 0) * (Number(line.unitCost) || 0);
              return (
                <div className="iproc-line-row iproc-line-row--recv" key={index}>
                  <label className="iproc-field">
                    <span>{index === 0 ? 'Item Description' : ''}</span>
                    <select
                      value={line.itemId}
                      onChange={(e) => setLine(index, 'itemId', e.target.value)}
                    >
                      <option value="">Custom / type below</option>
                      {catalogItems.map((it) => {
                        const cost = catalogUnitCost(it);
                        return (
                          <option key={it.id} value={it.id}>
                            {it.name}
                            {it.code ? ` (${it.code})` : ''}
                            {cost > 0 ? ` — ${formatAUD(cost)}` : ''}
                          </option>
                        );
                      })}
                    </select>
                    <input
                      value={line.description}
                      onChange={(e) => setLine(index, 'description', e.target.value)}
                      placeholder="Item description"
                    />
                  </label>
                  <label className="iproc-field">
                    <span>{index === 0 ? 'Qty' : ''}</span>
                    <input
                      type="number"
                      min="1"
                      value={line.quantity}
                      onChange={(e) => setLine(index, 'quantity', e.target.value)}
                    />
                  </label>
                  <label className="iproc-field">
                    <span>{index === 0 ? 'Received' : ''}</span>
                    <input
                      type="number"
                      min="0"
                      value={line.quantityReceived}
                      onChange={(e) => setLine(index, 'quantityReceived', e.target.value)}
                    />
                  </label>
                  <label className="iproc-field">
                    <span>{index === 0 ? 'Unit Cost' : ''}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unitCost}
                      onChange={(e) => setLine(index, 'unitCost', e.target.value)}
                      placeholder="0.00"
                    />
                  </label>
                  <label className="iproc-field">
                    <span>{index === 0 ? 'Total' : ''}</span>
                    <input value={formatAUD(lineTotal)} readOnly />
                  </label>
                  <button
                    type="button"
                    className="iproc-icon-btn danger"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        lines:
                          prev.lines.length > 1
                            ? prev.lines.filter((_, i) => i !== index)
                            : [emptyLine()],
                      }))
                    }
                  >
                    <FaTrash />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="iproc-add-line"
              onClick={() =>
                setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }))
              }
            >
              <FaPlus /> Add Another Item
            </button>
            <div className="iproc-totals">
              <div><span>Subtotal</span><span>{formatAUD(totals.subtotal)}</span></div>
              <div><span>GST (10%)</span><span>{formatAUD(totals.gst)}</span></div>
              <div><b>Total</b><b>{formatAUD(totals.total)}</b></div>
            </div>
          </section>

          <section>
            <h3><FaFileAlt /> Notes</h3>
            <div className="iproc-grid">
              <label className="iproc-field iproc-field--full">
                <span>Delivery Instructions</span>
                <input
                  value={form.deliveryInstructions}
                  onChange={(e) => set('deliveryInstructions', e.target.value)}
                  placeholder="Special delivery instructions"
                />
              </label>
              <label className="iproc-field iproc-field--full">
                <span>Notes</span>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="Optional notes"
                />
              </label>
            </div>
          </section>

          <div className="iproc-modal-footer">
            <button type="button" className="iproc-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="iproc-btn iproc-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : editData ? 'Save changes' : 'Create PO'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
